import React, { useEffect, useRef, useState } from 'react';
import { sportsApi } from './sportsApi';
import { displayTime } from './sportsTime';
const MAX_BYTES = 50 * 1024 * 1024;
export async function downloadEpg(siteId, { provider, channelId } = {}) {
  const query = new URLSearchParams({ ...(provider ? { provider } : {}), ...(channelId ? { channelId } : {}) });
  const result = await sportsApi(siteId, `epg-transfer/export?${query}`);
  const url = URL.createObjectURL(new Blob([result.content], { type: 'application/xml;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url; link.download = result.filename; document.body.appendChild(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return result.skipped || [];
}
export function EpgDownloadButton({ siteId, provider, channelId, label = 'Download provider XMLTV', disabled = false }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  return <><button type="button" disabled={busy || disabled} onClick={async () => {
    setBusy(true); setError('');
    try { await downloadEpg(siteId, { provider, channelId }); } catch (failure) { setError(failure.message); }
    finally { setBusy(false); }
  }}>{busy ? 'Preparing download…' : label}</button>{error && <p role="alert">{error}</p>}</>;
}
export default function EpgTransfer({ siteId, timeZone, onUpdated, onAccess }) {
  const [canManage, setCanManage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const input = useRef(null);
  useEffect(() => {
    let active = true;
    sportsApi(siteId, 'epg-transfer/access').then((result) => {
      if (active) { setCanManage(result.canManage); onAccess?.(result.canManage); }
    }).catch(() => { if (active) { setCanManage(false); onAccess?.(false); } });
    return () => { active = false; };
  }, [siteId, onAccess]);
  if (!canManage) return null;
  const run = async (work) => {
    setBusy(true); setError(''); setMessage('');
    try { await work(); } catch (failure) { setError(failure.message); }
    finally { setBusy(false); }
  };
  return <details className="sports-calendar__tools"><summary>Download / upload EPG data · Admin</summary>
    <p>Transfer shared provider guides between Suitestream installations. Imports affect every venue using that provider. Venue channel choices and player connections are kept.</p>
    <button type="button" disabled={busy} onClick={() => run(async () => {
      const skipped = await downloadEpg(siteId);
      setMessage(`XMLTV download ready.${skipped.length ? ` No published guide to export: ${skipped.join(', ')}.` : ''}`);
    })}>Download all providers XMLTV</button>
    <label>Upload Suitestream XMLTV export<input ref={input} type="file" accept=".xml,.xmltv,application/xml,text/xml" disabled={busy} onChange={(event) => {
      const selected = event.target.files?.[0]; setFile(null); setPreview(null); setError(''); setMessage('');
      if (!selected) return;
      run(async () => {
        if (selected.size > MAX_BYTES) throw new Error('Choose an EPG export no larger than 50 MB.');
        const content = await selected.text();
        const result = await sportsApi(siteId, 'epg-transfer/preview', { method: 'POST', body: { content } });
        setFile({ name: selected.name, content }); setPreview(result);
      });
    }} /></label>
    {preview && file && <div aria-label="EPG import preview">
      <p><strong>{file.name}</strong> · {preview.services.length} providers</p>
      <ul>{preview.services.map((service) => <li key={service.provider}><strong>{service.label}</strong> · {service.channels} channels · {service.programmes} programmes
        <p>{service.createsProvider ? 'Creates this provider on this installation.' : service.scope === 'channels' ? 'Restores only included channels; other channels are kept.' : 'Replaces this provider’s complete published guide; omitted channels become unavailable.'}</p>
        <p>Collected: {displayTime(service.fetchedAt, timeZone)}. Listings: {service.observedStart ? `${displayTime(service.observedStart, timeZone)} – ${displayTime(service.observedEnd, timeZone)}` : 'No programmes'}. Gaps may exist.</p>
        {service.older && <p role="alert">This file is older than the installed guide and cannot be applied.</p>}
      </li>)}</ul>
      <p>Applying pauses automatic refresh for the included providers and clears queued refresh requests. An active collection must finish first. You can resume automatic updates in each provider’s settings.</p>
      <button type="button" disabled={busy || preview.services.some((service) => service.older)} onClick={() => run(async () => {
        await sportsApi(siteId, 'epg-transfer/import', { method: 'POST', body: { content: file.content,
          expectedRuns: Object.fromEntries(preview.services.map((service) => [service.provider, service.expectedRunId])) } });
        setFile(null); setPreview(null); if (input.current) input.current.value = '';
        setMessage('EPG imported. Automatic refresh is paused for the imported providers.'); onUpdated?.();
      })}>Apply EPG import</button>
      <button type="button" disabled={busy} onClick={() => { setFile(null); setPreview(null); if (input.current) input.current.value = ''; }}>Cancel import</button>
    </div>}
    {busy && <p role="status">Processing EPG data…</p>}
    {message && <p role="status">{message}</p>}{error && <p role="alert">{error}</p>}
  </details>;
}
